import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  Output,
  ViewChild,
  inject,
} from '@angular/core';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Store } from '@ngrx/store';
import * as moment from 'moment';
import { Observable, filter, map, take } from 'rxjs';

import { PuSubscribable } from '@app/utils/pu-subscribe';
import { CiteElementDisplayMode } from '@main-application/inspections/models/rest-inspections-model.interface';
import {
  CustomFieldDestination,
  CustomFieldType,
  CustomFieldsService,
} from '@main-application/management/pages/property-model/services/custom-fields.service';
import {
  selectCustomFields,
  selectTemplatePlaceholders,
} from '@main-application/management/store/management.selectors';
import { RadioListOrientation } from '@main-application/resident/pages/inspect/inspect.model';
import {
  AreaPageElementQuestionModel,
  CustomFieldTypeMap,
} from '@main-application/shared/template-editor-dialog/models/template-editor-model';
import {
  TemplateEditorDialogService,
  TemplatePlaceholder,
} from '@main-application/shared/template-editor-dialog/services/template-editor-dialog.service';
import { EColorPalette } from '@shared/enums/color-palette.enum';
import { EIcon } from '@shared/enums/icon.enum';
import { IRadioButtonOption } from '@shared/interfaces/radio-button-option.interface';
import { DialogResult } from '@ui-components/modals/config/dialog-result.enum';
import { ModalsService } from '@ui-components/modals/modals.service';

import { TemplateEditorDialogItemComponent } from '../template-editor-item.component';
import { templateEditorQuestionsPlaceholders } from './template-editor-question.consts';

@UntilDestroy()
@Component({
  selector: 'app-template-editor-question-item',
  templateUrl: './template-editor-question.component.html',
  styleUrls: ['./template-editor-question.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TemplateEditorDialogQuestionComponent extends PuSubscribable implements AfterViewInit {
  EColorPalette = EColorPalette;
  protected readonly EIcon = EIcon;
  CustomFieldType = CustomFieldType;
  RadioListOrientation = RadioListOrientation;

  private store = inject(Store);
  private fb: FormBuilder = inject(FormBuilder);
  private cdr: ChangeDetectorRef = inject(ChangeDetectorRef);
  private templateEditorDialogService: TemplateEditorDialogService = inject(TemplateEditorDialogService);
  private modalsService: ModalsService = inject(ModalsService);
  private templateEditorDialogItemComponent: TemplateEditorDialogItemComponent = inject(
    TemplateEditorDialogItemComponent
  );
  private customFieldsService: CustomFieldsService = inject(CustomFieldsService);

  @Input() isDefaultTempateType = true;
  @Input() set index(value: number) {
    if (this.questionItem.type === 'boolean') {
      this.placeholder =
        templateEditorQuestionsPlaceholders.boolean[
          Math.min(templateEditorQuestionsPlaceholders.boolean?.length - 1, value)
        ];
    } else {
      const list = templateEditorQuestionsPlaceholders[this.questionItem.type];
      if (list?.length) this.placeholder = list[value % list.length];
    }

    this.cdr.markForCheck();
  }

  private _questionItem: AreaPageElementQuestionModel;
  public isEditingSetting = false;
  public isChoosingFieldType = false;
  public isChoosingCustomFieldType = false;
  public selectedFieldType:
    | 'number'
    | 'boolean'
    | 'text'
    | 'money'
    | 'int'
    | 'user'
    | 'date'
    | 'dateTime'
    | 'checkList'
    | 'radiolist'
    | 'dropdown' = 'boolean';

  public typeLabelMap: Record<string, string> = {
    boolean: 'Boolean',
    text: 'Text',
    money: 'Amount',
    number: 'Decimal',
    int: 'Integer',
    user: 'User',
    date: 'Date (only)',
    dateTime: 'Date & Time',
    checkList: 'Check List',
    radiolist: 'Radio List',
    dropdown: 'Drop-down list',
  };

  public selectedCustomFieldId: number = null;

  public fieldTypeOptions: IRadioButtonOption<CustomFieldType>[] = Object.values(CustomFieldType)
    .filter(value => typeof value === 'number')
    .map(value => {
      let label = CustomFieldType[value as CustomFieldType];

      if (label === 'DropDown') {
        label = 'Dropdown';
      } else if (label === 'DateTime') {
        label = 'Date & Time';
      }

      return {
        value: value as CustomFieldType,
        label,
      };
    });

  public customFieldTypeOptions: IRadioButtonOption<number>[] = [];

  public isDefaultMenuOpen = false;
  public currentDefaultLabel = null;
  public currentDefaultLabelExtraCount = null;

  public isVarianceMenuOpen = false;
  public currentVarianceLabel = null;
  public varianceOptions = [
    { id: CiteElementDisplayMode.Value, label: 'Default' },
    { id: CiteElementDisplayMode.Variance, label: 'Variance' },
  ];

  public radioOptions: IRadioButtonOption<string>[] = [];
  public orientationOptions: IRadioButtonOption<RadioListOrientation>[] = [
    { value: RadioListOrientation.Vertical, label: 'Vertically' },
    { value: RadioListOrientation.Horizontal, label: 'Horizontally' },
  ];

  @Input() set questionItem(value: AreaPageElementQuestionModel) {
    this._questionItem = value;
    this.initQuestion(value);
    this.initForm(value);

    if (value?.type === 'radiolist' && Array.isArray(value.options)) {
      this.radioOptions = value.options.map(o => ({
        label: o.label,
        value: o.value,
      }));
    }

    if ((value.type === 'checkList' || value.type === 'radiolist') && value?.options?.length) {
      setTimeout(() => {
        value.options.forEach((opt, i) => {
          const span = document.createElement('span');
          span.className = 'title-hidden';
          span.innerText = opt.label && opt.label?.trim() !== '' ? opt.label : this.optionPlaceholder;
          document.body.appendChild(span);
          this.optionWidths[i] = `${span.offsetWidth}px`;
          document.body.removeChild(span);
        });
        this.cdr.markForCheck();
      });
    }
  }

  get questionItem(): AreaPageElementQuestionModel {
    return this._questionItem;
  }

  @Output() enterPressed = new EventEmitter<AreaPageElementQuestionModel>();
  @Output() questionItemChange = new EventEmitter<AreaPageElementQuestionModel>();
  @Output() remove = new EventEmitter<AreaPageElementQuestionModel>();

  placeholders: TemplatePlaceholder[];
  placeholder = templateEditorQuestionsPlaceholders.boolean[0];

  @ViewChild('questionTitleElement', { read: ElementRef, static: false })
  declare questionTitleElement: ElementRef<HTMLSpanElement>;
  @ViewChild('inputElement', { read: ElementRef, static: false }) inputElement: ElementRef<HTMLInputElement>;

  declare questionWidth: string;
  optionPlaceholder = 'Enter label';
  public optionWidths: { [key: number]: string } = {};
  isViewInited = false;

  public questionForm: FormGroup;

  constructor() {
    super();
  }

  ngAfterViewInit() {
    this.store
      .select(selectTemplatePlaceholders)
      .pipe(filter(Boolean), take(1))
      .subscribe(placeholders => {
        this.placeholders = placeholders;
        this.updateDefaultLabel(this.questionItem);
      })
      .untilDestroyed(this);

    this.store
      .select(selectCustomFields)
      .pipe(
        filter(fields => !!fields?.length),
        take(1)
      )
      .subscribe(fields => {
        this.customFieldTypeOptions = fields.map(field => ({
          value: field.id,
          label: `${this.fromBitmaskToNames(field.appliesToMask)}: ${field.name}`,
        }));
        this.cdr.markForCheck();
      })
      .untilDestroyed(this);

    this.isViewInited = true;
    this.setQuestionValue(this.questionItem?.title || this.placeholder);

    this.templateEditorDialogService.questionIdFocused$
      .pipe(untilDestroyed(this))
      .subscribe(id => {
        if (id === this.questionItem.guid) {
          this.inputElement.nativeElement.focus({
            preventScroll: true,
          });
        }
      })
      .untilDestroyed(this);

    this.templateEditorDialogItemComponent.expandQuestion$
      .pipe(untilDestroyed(this))
      .subscribe(token => {
        const [guid, action] = token.split(':');
        if (guid === this.questionItem.guid) {
          if (action === 'openTypeSelector') {
            this.isEditingSetting = true;
            this.isChoosingFieldType = true;
            this.isChoosingCustomFieldType = false;
          } else if (action === 'openCustomFieldSelector') {
            this.isEditingSetting = true;
            this.isChoosingFieldType = false;
            this.openCustomFieldSelection();
          } else {
            this.isEditingSetting = true;
            this.isChoosingCustomFieldType = false;
          }
        } else {
          this.isEditingSetting = false;
          this.isChoosingFieldType = false;
          this.isChoosingCustomFieldType = false;
        }
        this.cdr.markForCheck();
      })
      .untilDestroyed(this);
  }

  private initQuestion(value: AreaPageElementQuestionModel) {
    this.placeholder = this.getPlaceholder(value);
    if (this.isViewInited) {
      this.setQuestionValue(value?.title || this.placeholder);
    }

    this.updateDefaultLabel(value);
    this.updateVarianceLabel(value);
  }

  private initForm(question: AreaPageElementQuestionModel): void {
    const isCustomFieldBinding = !!question?.customFieldBinding;
    const initialIsRequired = isCustomFieldBinding ? question?.isRequired === true : question?.isRequired !== false;

    this.questionForm = this.fb.group({
      isRequired: new FormControl(initialIsRequired),
      orientation: new FormControl(
        question?.orientation === RadioListOrientation.Horizontal
          ? RadioListOrientation.Horizontal
          : RadioListOrientation.Vertical
      ),
      defaultValue: new FormControl(
        question?.type === 'checkList'
          ? typeof question?.defaultValue === 'string'
            ? question.defaultValue.split(',')
            : []
          : question?.type === 'radiolist'
          ? typeof question?.defaultValue === 'string'
            ? question.defaultValue
            : null
          : question?.defaultValue ?? null
      ),
      photoRequiresForAllitems: new FormControl(question?.photoRequiresForAllitems),
      commentsRequiresForAllitems: new FormControl(question?.commentsRequiresForAllitems),
      photoRequiresOn: new FormControl(question?.photoRequiresOn ? question.photoRequiresOn : []),
      commentsRequiresOn: new FormControl(question?.commentsRequiresOn ? question.commentsRequiresOn : []),
    });

    this.questionForm
      .get('isRequired')!
      .valueChanges.pipe(untilDestroyed(this))
      .subscribe(isRequired => {
        const updated: AreaPageElementQuestionModel = {
          ...this.questionItem,
          isRequired,
        };
        this._questionItem = updated;
        this.questionItemChange.emit(updated);
      })
      .untilDestroyed(this);

    if (this.questionItem.type === 'radiolist') {
      this.questionForm
        .get('defaultValue')!
        .valueChanges.pipe(untilDestroyed(this))
        .subscribe(value => {
          const updated: AreaPageElementQuestionModel = {
            ...this.questionItem,
            defaultValue: value,
          };
          this._questionItem = updated;
          this.questionItemChange.emit(updated);
        })
        .untilDestroyed(this);
    }
  }

  private getPlaceholder(value: AreaPageElementQuestionModel): string {
    const type = value?.type || 'boolean';
    const list = templateEditorQuestionsPlaceholders[type] || [];
    const index = this.index ?? 0;

    const idx = type === 'boolean' ? Math.min(index, list.length - 1) : list.length > 0 ? index % list.length : 0;

    return list[idx] || '';
  }

  private setQuestionValue(question: string) {
    this.questionTitleElement.nativeElement.textContent = question;
    this.questionWidth = this.questionTitleElement.nativeElement.offsetWidth + 'px';
    this.cdr.detectChanges();
  }

  private fromBitmask(mask: number): CustomFieldDestination[] {
    return Object.values(CustomFieldDestination)
      .filter(v => typeof v === 'number')
      .filter(v => (mask & (v as number)) !== 0) as CustomFieldDestination[];
  }

  private fromBitmaskToNames(mask: number): string {
    return Object.entries(CustomFieldDestination)
      .filter(([key, value]) => isNaN(Number(key)))
      .map(([key, value]) => ({ key, value: value as number }))
      .filter(entry => (mask & entry.value) !== 0)
      .sort((a, b) => b.value - a.value)
      .map(entry => entry.key)
      .join(', ');
  }

  public removeClick() {
    this.remove.emit(this.questionItem);
  }

  public toggleDefaultValue(value: boolean) {
    const { defaultValue, ...rest } = this.questionItem;
    if (defaultValue === undefined || defaultValue !== value) {
      this.questionItemChange.emit({ ...rest, defaultValue: value });
    } else {
      this.questionItemChange.emit({ ...rest });
    }
  }

  public questionChange(event: FocusEvent) {
    const value = event.target instanceof HTMLInputElement && event.target.value;
    if (value === this.questionItem.title) {
      return;
    }
    this.questionItemChange.emit({ ...this.questionItem, title: value, name: this.questionItem.guid + value });
  }

  public keyPressed($event: KeyboardEvent) {
    if ($event.key === 'Enter') {
      const value = $event.target instanceof HTMLInputElement && $event.target.value;
      this.enterPressed.emit({ ...this.questionItem, title: value, name: this.questionItem.guid + value });
      $event.stopPropagation();
      $event.preventDefault();
    }
  }

  public updateWidth(event: Event) {
    if (!(event instanceof InputEvent) || !(event.target instanceof HTMLInputElement)) {
      return;
    }
    let value = event.target.value; //NOET-9631 allow to enter ? .replace(/\?/g, ''); // + '?'; NOET-8453 removed question mark
    value = value === '?' ? '' : value;
    if (value !== event.target.value) {
      event.target.value = value;
      event.target.selectionStart = event.target.selectionEnd = value.length - 1;
    }
    this.questionTitleElement.nativeElement.textContent = value || this.placeholder;

    this.questionWidth = this.questionTitleElement.nativeElement.offsetWidth + 'px';
  }

  public onFieldTypeSelect(
    newFieldType:
      | 'number'
      | 'boolean'
      | 'text'
      | 'money'
      | 'int'
      | 'user'
      | 'date'
      | 'dateTime'
      | 'checkList'
      | 'radiolist'
      | 'dropdown'
      | 'customField'
  ) {
    if (newFieldType === 'customField') {
      this.isEditingSetting = false;
      this.isChoosingCustomFieldType = true;
      return;
    }

    if (newFieldType === 'boolean') {
      this.placeholder =
        templateEditorQuestionsPlaceholders.boolean[
          Math.min(templateEditorQuestionsPlaceholders.boolean?.length - 1, this.index)
        ];
    } else {
      const list = templateEditorQuestionsPlaceholders[newFieldType];
      if (list?.length) this.placeholder = list[3 % list.length];
    }

    if (this.questionItem.type !== newFieldType) {
      const from = this.questionItem.type;
      const to = newFieldType;

      let newDefaultValue: string | null = null;

      if (from === 'checkList' && to === 'dropdown') {
        const raw = this.questionItem.defaultValue;
        const first =
          typeof raw === 'string' && raw.includes(',')
            ? raw
                .split(',')
                .map(v => v.trim())
                .filter(Boolean)[0]
            : typeof raw === 'string'
            ? raw.trim()
            : null;

        newDefaultValue = first || null;
      } else if (from === 'dropdown' && to === 'checkList') {
        newDefaultValue =
          typeof this.questionItem.defaultValue === 'string' ? this.questionItem.defaultValue.trim() || null : null;
      } else {
        newDefaultValue = null;
      }

      this.questionItemChange.emit({
        ...this.questionItem,
        type: newFieldType,
        defaultValue: newDefaultValue,
        displayMode: null,
        customFieldBinding: undefined,
      });
    }

    this.selectedFieldType = newFieldType;
    this.isChoosingFieldType = false;
  }

  public onDefaultMenuOpened(): void {
    this.isDefaultMenuOpen = true;
    this.cdr.markForCheck();
  }

  public onDefaultMenuClosed(): void {
    this.isDefaultMenuOpen = false;
    this.cdr.markForCheck();
  }

  public onVarianceMenuOpened(): void {
    this.isVarianceMenuOpen = true;
    this.cdr.markForCheck();
  }

  public onVarianceMenuClosed(): void {
    this.isVarianceMenuOpen = false;
    this.cdr.markForCheck();
  }

  public toggleSetting(expand?: boolean): void {
    if (expand === true) {
      this.isEditingSetting = true;
    } else if (expand === false) {
      this.isEditingSetting = false;
    } else {
      this.isEditingSetting = !this.isEditingSetting;
    }

    this.isChoosingCustomFieldType = false;

    if (this.isEditingSetting || this.isChoosingCustomFieldType) {
      this.templateEditorDialogItemComponent.expandQuestion$.next(this.questionItem.guid);
    }
  }

  public toggleCustomFieldTypeChange(): void {
    this.isChoosingFieldType = !this.isChoosingFieldType;
  }

  public onCustomFieldTypeChange(customFieldBinding: number) {
    this.store
      .select(selectCustomFields)
      .pipe(
        filter(fields => !!fields?.length),
        take(1)
      )
      .subscribe(fields => {
        const field = fields.find(f => f.id === customFieldBinding);

        if (!field) {
          this.isEditingSetting = false;
          this.isChoosingCustomFieldType = false;
          return;
        }

        const fieldType = CustomFieldTypeMap[field.customFieldType];
        const fieldName = field.name;

        let defaultValue: boolean | number | string | Date | null = null;

        switch (fieldType.toLowerCase()) {
          case 'boolean':
            defaultValue = field.defaultValueBool;
            break;
          case 'int':
            defaultValue = field.defaultValueInt;
            break;
          case 'number':
            defaultValue = field.defaultValueNumber;
            break;
          case 'money':
            defaultValue = field.defaultValueMoney;
            break;
          case 'user':
            defaultValue = field.defaultValueUserId;
            break;
          case 'date':
            defaultValue = field.defaultValueDate;
            break;
          case 'datetime':
            defaultValue = field.defaultValueDateTime;
            break;
          case 'dropdown':
            defaultValue = field.defaultValueDropDown;
            break;
          case 'checklist':
            defaultValue = field.defaultValueCheckList;
            break;
          case 'radiolist':
            defaultValue = field.defaultValueRadioList;
            break;
          default:
            defaultValue = null;
        }

        this.questionItemChange.emit({
          ...this.questionItem,
          customFieldBinding,
          name: fieldName,
          title: fieldName,
          type: fieldType,
          defaultValue,
          isRequired: false,
        });

        this.isEditingSetting = false;
        this.isChoosingCustomFieldType = false;
      })
      .untilDestroyed(this);
  }

  public onVarianceChange(displayMode: CiteElementDisplayMode): void {
    this.questionItemChange.emit({ ...this.questionItem, displayMode });
  }

  private updateDefaultLabel(question: AreaPageElementQuestionModel): void {
    if (!question) {
      this.currentDefaultLabel = null;
      return;
    }

    const { type, defaultValue } = question;

    if (defaultValue === null || defaultValue === undefined || defaultValue === '') {
      this.currentDefaultLabel = null;
      return;
    }

    if (
      this.placeholders?.length > 0 &&
      this.placeholders?.find(placeholder => placeholder.value === question.defaultValue)
    ) {
      this.currentDefaultLabel = this.placeholders?.find(
        placeholder => placeholder.value === question.defaultValue
      ).label;
    } else if (type === 'boolean') {
      this.currentDefaultLabel = defaultValue === true ? 'Yes' : defaultValue === false ? 'No' : null;
      return;
    } else {
      switch (type?.toLowerCase()) {
        case 'text':
          {
            const str = defaultValue.toString();
            this.currentDefaultLabel = str.length > 36 ? str.slice(0, 36) + '…' : str;
          }
          break;

        case 'int':
        case 'number':
          this.currentDefaultLabel = defaultValue.toString();
          break;

        case 'money':
          this.currentDefaultLabel = this.customFieldsService.formatAmount(
            typeof defaultValue === 'number'
              ? defaultValue
              : this.customFieldsService.parseAmount(defaultValue.toString())
          );
          break;

        case 'dropdown':
          if (question.options && Array.isArray(question.options)) {
            const match = question.options.find(opt => opt.value === defaultValue);
            if (match?.label) {
              const str = match.label.toString();
              this.currentDefaultLabel = str.length > 18 ? str.slice(0, 18) + '…' : str;
            } else {
              this.currentDefaultLabel = null;
            }
          } else {
            this.currentDefaultLabel = null;
          }
          break;

        case 'checklist': {
          const values =
            typeof defaultValue === 'string'
              ? defaultValue
                  .split(',')
                  .map(v => v.trim())
                  .filter(Boolean)
              : [];

          if (!values.length) {
            this.currentDefaultLabel = null;
            this.currentDefaultLabelExtraCount = null;
            break;
          }

          const first = values[0];
          const firstLabel = question.options?.find(o => o.value === first)?.label || first;
          const str = firstLabel.toString();
          const extraCount = values.length - 1;

          this.currentDefaultLabel = str.length > 18 ? str.slice(0, 18) + '…' : str;
          this.currentDefaultLabelExtraCount = extraCount > 0 ? extraCount : null;
          break;
        }

        case 'radiolist': {
          const values =
            typeof defaultValue === 'string'
              ? defaultValue
                  .split(',')
                  .map(v => v.trim())
                  .filter(Boolean)
              : [];

          if (!values.length) {
            this.currentDefaultLabel = null;
            this.currentDefaultLabelExtraCount = null;
            break;
          }

          const first = values[0];
          const firstLabel = question.options?.find(o => o.value === first)?.label || first;
          const str = firstLabel.toString();
          const extraCount = values.length - 1;

          this.currentDefaultLabel = str.length > 18 ? str.slice(0, 18) + '…' : str;
          this.currentDefaultLabelExtraCount = extraCount > 0 ? extraCount : null;
          break;
        }

        case 'date':
          if (typeof defaultValue === 'string' || typeof defaultValue === 'number' || defaultValue instanceof Date) {
            this.currentDefaultLabel = moment(defaultValue).format('MM/DD/YYYY');
          } else {
            this.currentDefaultLabel = null;
          }
          break;

        case 'datetime':
          if (typeof defaultValue === 'string' || typeof defaultValue === 'number' || defaultValue instanceof Date) {
            this.currentDefaultLabel = moment(defaultValue).format('MM/DD/YYYY hh:mm A');
          } else {
            this.currentDefaultLabel = null;
          }
          break;

        default:
          this.currentDefaultLabel = 'Custom';
      }
    }
  }

  private updateVarianceLabel(question: AreaPageElementQuestionModel): void {
    const option = this.varianceOptions?.find(v => v.id === question?.displayMode);
    this.currentVarianceLabel = option?.label;
  }

  public setDefault(question: AreaPageElementQuestionModel, defaultValue?: 'default' | boolean | null): void {
    if (!question) return;

    if (defaultValue === null) {
      this.questionItemChange.emit({ ...this.questionItem, defaultValue });
      this.currentDefaultLabel = null;
      return;
    }

    if (defaultValue === 'default') {
      this.openValueEditor(
        { ...this.questionItem, defaultValue: this.questionItem.defaultValue },
        this.placeholders,
        true
      );
      return;
    }

    if (question.type === 'boolean') {
      this.questionItemChange.emit({ ...this.questionItem, defaultValue });
      this.currentDefaultLabel = defaultValue === true ? 'Yes' : defaultValue === false ? 'No' : null;
      this.cdr.markForCheck();
      return;
    }

    this.openValueEditor({ ...this.questionItem, defaultValue: this.questionItem.defaultValue }, this.placeholders);
  }

  public chooseCustomFieldIcon(questionItem: AreaPageElementQuestionModel): Observable<string> {
    const customFieldId = questionItem.customFieldBinding;

    return this.store.select(selectCustomFields).pipe(
      filter(fields => !!fields?.length),
      map(fields => {
        const bitMask = fields.find(f => f.id === customFieldId)?.appliesToMask ?? 0;
        const values = this.fromBitmask(bitMask);
        const max = Math.max(...values);

        switch (max) {
          case CustomFieldDestination.Unit:
            return 'door_front';
          default:
            return 'home';
        }
      }),
      take(1)
    );
  }

  public chooseCustomFieldName(questionItem: AreaPageElementQuestionModel): Observable<string> {
    const customFieldId = questionItem.customFieldBinding;
    if (!customFieldId) return;

    return this.store.select(selectCustomFields).pipe(
      filter(fields => !!fields?.length),
      map(fields => {
        const bitMask = fields.find(f => f.id === customFieldId)?.appliesToMask ?? 0;
        const values = this.fromBitmask(bitMask);

        const nameMap: Record<number, string> = {
          1: 'Unit',
          2: 'Property',
          4: 'Portfolio',
        };

        const names = values
          .sort((a, b) => b - a)
          .map(v => nameMap[v])
          .filter(Boolean);

        const title = fields.find(f => f.id === customFieldId)?.name;

        return `${names.join(', ')}: ${title}`;
      }),
      take(1)
    );
  }

  public openCustomFieldSelection(): void {
    this.selectedCustomFieldId = this.questionItem.customFieldBinding;
    this.isChoosingCustomFieldType = true;
  }

  public cancelCustomFieldSelection(): void {
    this.isChoosingCustomFieldType = false;
  }

  public openDropdownOptionsEditor(questionItem: AreaPageElementQuestionModel): void {
    this.modalsService
      .openDropdownOptionsEditor(questionItem)
      .afterClosed()
      .pipe(take(1))
      .subscribe(result => {
        if (result?.result === DialogResult.Success && result.data) {
          this.questionItemChange.emit({
            ...this.questionItem,
            title: result.data?.title,
            name: result.data?.title,
            options: result.data.options,
            defaultValue: result.data.defaultValue,
          });
        }
      })
      .untilDestroyed(this);
  }

  private openValueEditor(
    questionItem: AreaPageElementQuestionModel,
    placeholders: TemplatePlaceholder[],
    useDynamicValue = false
  ): void {
    this.modalsService
      .openValueEditor(questionItem, placeholders, useDynamicValue)
      .afterClosed()
      .pipe(take(1))
      .subscribe(result => {
        if (result?.result === DialogResult.Success && result?.data) {
          const defaultValueRaw = result.data.question?.defaultValue;

          const defaultValue =
            defaultValueRaw === null ||
            defaultValueRaw === undefined ||
            (typeof defaultValueRaw === 'string' && defaultValueRaw?.trim() === '')
              ? null
              : defaultValueRaw;

          this.questionItemChange.emit({
            ...this.questionItem,
            title: result.data?.question?.title,
            defaultValue,
          });
        }
      })
      .untilDestroyed(this);
  }

  public setPhotoRequired(optionValue: string): void {
    const current: string[] = this.questionForm.get('photoRequiresOn')?.value ?? [];
    const options = this.questionItem?.options?.map(o => o.value) ?? [];
    let updated: string[];

    if (current.includes(optionValue)) {
      updated = current.filter(v => v !== optionValue);
    } else {
      updated = [...current, optionValue];
    }

    const allSelected = updated.length === options.length;

    this.questionForm.patchValue({
      photoRequiresOn: updated,
      photoRequiresForAllitems: allSelected,
    });

    this.questionItem = {
      ...this.questionItem,
      photoRequiresOn: updated,
      photoRequiresForAllitems: allSelected,
    };

    this.questionItemChange.emit(this.questionItem);
    this.cdr.markForCheck();
  }

  public setCommentsRequired(optionValue: string): void {
    const current: string[] = this.questionForm.get('commentsRequiresOn')?.value ?? [];
    const options = this.questionItem?.options?.map(o => o.value) ?? [];
    let updated: string[];

    if (current.includes(optionValue)) {
      updated = current.filter(v => v !== optionValue);
    } else {
      updated = [...current, optionValue];
    }

    const allSelected = updated.length === options.length;

    this.questionForm.patchValue({
      commentsRequiresOn: updated,
      commentsRequiresForAllitems: allSelected,
    });

    this.questionItem = {
      ...this.questionItem,
      commentsRequiresOn: updated,
      commentsRequiresForAllitems: allSelected,
    };

    this.questionItemChange.emit(this.questionItem);
    this.cdr.markForCheck();
  }

  public setPhotoRequiredForAll(): void {
    const current = this.questionForm.get('photoRequiresForAllitems')?.value;
    const options = this.questionItem.options?.map(o => o.value) ?? [];
    const newValue = !current;

    this.questionForm.patchValue({
      photoRequiresForAllitems: newValue,
      photoRequiresOn: newValue ? options : [],
    });

    this.questionItem = {
      ...this.questionItem,
      photoRequiresForAllitems: newValue,
      photoRequiresOn: newValue ? options : [],
    };

    this.questionItemChange.emit(this.questionItem);
    this.cdr.markForCheck();

    this.toggleSetting(true);
  }

  public setCommentsRequiredForAll(): void {
    const current = this.questionForm.get('commentsRequiresForAllitems')?.value;
    const options = this.questionItem?.options?.map(o => o.value) ?? [];
    const newValue = !current;

    this.questionForm.patchValue({
      commentsRequiresForAllitems: newValue,
      commentsRequiresOn: newValue ? options : [],
    });

    this.questionItem = {
      ...this.questionItem,
      commentsRequiresForAllitems: newValue,
      commentsRequiresOn: newValue ? options : [],
    };

    this.questionItemChange.emit(this.questionItem);
    this.cdr.markForCheck();

    this.toggleSetting(true);
  }

  public addOption(): void {
    const currentOptions = this.questionItem.options ?? [];

    const newIndex = currentOptions.length + 1;
    const newOption = {
      label: `Item (${newIndex})`,
      value: crypto.randomUUID(),
    };

    let updatedPhotoRequiresOn = [...(this.questionItem.photoRequiresOn ?? [])];
    let updatedCommentsRequiresOn = [...(this.questionItem.commentsRequiresOn ?? [])];

    if (this.questionItem.photoRequiresForAllitems) {
      updatedPhotoRequiresOn = [...updatedPhotoRequiresOn, newOption.value];
    }
    if (this.questionItem.commentsRequiresForAllitems) {
      updatedCommentsRequiresOn = [...updatedCommentsRequiresOn, newOption.value];
    }

    const updatedQuestion: AreaPageElementQuestionModel = {
      ...this.questionItem,
      options: [...currentOptions, newOption],
      photoRequiresOn: updatedPhotoRequiresOn,
      commentsRequiresOn: updatedCommentsRequiresOn,
    };

    this.questionItem = updatedQuestion;
    this.questionItemChange.emit(updatedQuestion);
    this.cdr.markForCheck();

    setTimeout(() => {
      const inputs = document.querySelectorAll<HTMLInputElement>('.checklist-label-input');
      const target = inputs[inputs.length - 1];
      target?.focus();
    });
  }

  public toggleDefaultOption(optionValue: string): void {
    const current: string[] = this.questionForm.get('defaultValue')?.value ?? [];
    let updated: string[];

    if (current.includes(optionValue)) {
      updated = current.filter(v => v !== optionValue);
    } else {
      updated = [...current, optionValue];
    }

    this.questionForm.patchValue({ defaultValue: updated });

    this.questionItem = {
      ...this.questionItem,
      defaultValue: updated.join(','),
    };

    this.questionItemChange.emit(this.questionItem);
    this.cdr.markForCheck();
  }

  public updateOptionLabel(index: number, event: Event): void {
    const input = event.target as HTMLInputElement;
    const newLabel = input?.value ?? '';

    const options = [...(this.questionItem.options ?? [])];
    options[index] = { ...options[index], label: newLabel };

    this.questionItem = { ...this.questionItem, options };
    this.questionItemChange.emit(this.questionItem);
    this.cdr.markForCheck();
  }

  public addOptionAfter(index: number): void {
    const currentOptions = this.questionItem.options ?? [];
    const newIndex = index + 1;
    const newOption = {
      label: `Item (${currentOptions.length + 1})`,
      value: crypto.randomUUID(),
    };

    const updatedPhotoRequiresOn = [...(this.questionItem.photoRequiresOn ?? [])];
    const updatedCommentsRequiresOn = [...(this.questionItem.commentsRequiresOn ?? [])];

    if (this.questionItem.photoRequiresForAllitems) {
      updatedPhotoRequiresOn.push(newOption.value);
    }
    if (this.questionItem.commentsRequiresForAllitems) {
      updatedCommentsRequiresOn.push(newOption.value);
    }

    const updatedOptions = [...currentOptions.slice(0, newIndex), newOption, ...currentOptions.slice(newIndex)];

    const updatedQuestion: AreaPageElementQuestionModel = {
      ...this.questionItem,
      options: updatedOptions,
      photoRequiresOn: updatedPhotoRequiresOn,
      commentsRequiresOn: updatedCommentsRequiresOn,
    };

    this.questionItem = updatedQuestion;
    this.questionItemChange.emit(updatedQuestion);
    this.cdr.markForCheck();

    setTimeout(() => {
      const inputs = document.querySelectorAll<HTMLInputElement>('.checklist-label-input');
      const target = inputs[newIndex];
      target?.focus();
    });
  }

  public updateOptionWidth(event: Event, index: number): void {
    const input = event.target as HTMLInputElement;
    const span = document.createElement('span');
    span.className = 'title-hidden';
    span.innerText = input.value && input.value?.trim() !== '' ? input.value : this.optionPlaceholder;
    document.body.appendChild(span);
    this.optionWidths[index] = `${span.offsetWidth}px`;
    document.body.removeChild(span);
  }

  public onDrop(event: CdkDragDrop<any[]>): void {
    if (!this.questionItem?.options) return;

    const updated = [...this.questionItem.options];
    moveItemInArray(updated, event.previousIndex, event.currentIndex);

    this.questionItem = { ...this.questionItem, options: updated };
    this.questionItemChange.emit(this.questionItem);

    setTimeout(() => {
      updated.forEach((opt, i) => {
        const span = document.createElement('span');
        span.className = 'title-hidden';
        span.innerText = opt.label && opt.label?.trim() !== '' ? opt.label : this.optionPlaceholder;
        document.body.appendChild(span);
        this.optionWidths[i] = `${span.offsetWidth + 10}px`;
        document.body.removeChild(span);
      });
      this.cdr.markForCheck();
    });
  }

  public removeOption(index: number): void {
    if (!this.questionItem?.options && this.questionItem.type !== 'checkList') return;

    const options = [...this.questionItem.options];
    options.splice(index, 1);

    let updatedDefaultValue: string | null = null;

    const defaultsFromForm: string[] = this.questionForm.get('defaultValue')?.value ?? [];
    const validDefaults = options.map(o => o.value).filter(v => defaultsFromForm.includes(v));
    updatedDefaultValue = validDefaults.length ? validDefaults.join(',') : null;

    const photoRequiresOn: string[] = options
      .map(o => o.value)
      .filter(v => (this.questionForm.get('photoRequiresOn')?.value ?? []).includes(v));

    const commentsRequiresOn: string[] = options
      .map(o => o.value)
      .filter(v => (this.questionForm.get('commentsRequiresOn')?.value ?? []).includes(v));

    this.questionItem = {
      ...this.questionItem,
      options,
      defaultValue: updatedDefaultValue,
      photoRequiresOn,
      commentsRequiresOn,
    };

    this.optionWidths = {};
    options.forEach((opt, i) => {
      const span = document.createElement('span');
      span.className = 'title-hidden';
      span.innerText = opt.label && opt.label?.trim() !== '' ? opt.label : this.optionPlaceholder;
      document.body.appendChild(span);
      this.optionWidths[i] = `${span.offsetWidth}px`;
      document.body.removeChild(span);
    });

    this.updateDefaultLabel(this.questionItem);

    this.questionItemChange.emit(this.questionItem);
    this.cdr.markForCheck();
  }

  public clearRadiolistDefault(): void {
    if (this.questionItem?.type !== 'radiolist') return;

    this.questionForm.get('defaultValue')?.setValue(null, { emitEvent: false });

    const updated: AreaPageElementQuestionModel = {
      ...this.questionItem,
      defaultValue: null,
    };

    this._questionItem = updated;
    this.updateDefaultLabel(updated);
    this.questionItemChange.emit(updated);
    this.cdr.markForCheck();
  }

  public isOrientationMenuOpen = false;

  public onOrientationMenuOpened(): void {
    this.isOrientationMenuOpen = true;
    this.cdr.markForCheck();
  }

  public onOrientationMenuClosed(): void {
    this.isOrientationMenuOpen = false;
    this.cdr.markForCheck();
  }

  public setOrientation(value: RadioListOrientation): void {
    this.questionForm.patchValue({ orientation: value });

    this.questionItem = {
      ...this.questionItem,
      orientation: value,
    };

    this.questionItemChange.emit(this.questionItem);
    this.cdr.markForCheck();
  }
}
